import { Component, Injector } from '@angular/core';
import { Location, registerLocaleData } from '@angular/common';
import {} from '@kpd/framework-ngx';
import {
  BreadCrumbService,
  EventAggregator,
  InputErrorComponent,
  Navigator,
  tr,
  EntityResolverService,
  Crumb,
  BrowserHistoryAndTitleManager,
  Key,
  AppBaseComponent,
} from '@kpd/framework-ngx';
import { ClarityIcons } from '@clr/icons';
import { ClrShapeOrganization, ClrShapeHistory } from '@clr/icons/shapes/essential-shapes';
import { ClrShapeArrow, ClrShapeAngle, ClrShapeBars, ClrShapeErrorStandard } from '@clr/icons/shapes/core-shapes';
import { ClrShapeImport, ClrShapeLink, ClrShapeUnlink } from '@clr/icons/shapes/technology-shapes';
import { MessageService } from 'primeng/components/common/messageservice';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, IMenuItem } from '@kpd/framework-ngx';
import { authConfig } from './auth.config';
import { Router, ActivatedRoute } from '@angular/router';
import { entityResolvers } from 'app/app.entity.resolvers';
import { Observable, of, Subject } from 'rxjs';
import { environment, apiUrl } from 'environments/environment';
import * as log from 'loglevel';
import { UserService } from 'app/shared/services/user.service';
import { User } from 'app/shared/models/user';
import { exhaustMap, filter, tap } from 'rxjs/operators';
import { AuthConfig } from 'angular-oauth2-oidc';
import { Permissions, PERMISSIONS } from './shared/services/permissions';
import { Tenant } from './shared/models/tenant';
import de from '@angular/common/locales/de';
import fr from '@angular/common/locales/fr';
import { TenantService } from './shared/services/tenant.service';
import { UMLocalizationService } from './shared/services/localization.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent extends AppBaseComponent {
  crumbs$: Observable<Crumb[]>;
  tenantName: string;
  menuItems: IMenuItem[];
  ddToggle: boolean;
  user = new User();
  environment = environment;
  switchTenant: string | null;
  userHasMoreTenants = false;
  exportUsers$ = new Subject();
  loading = false;

  protected get authPagePath(): string {
    return '/auth';
  }

  protected get logoutPath(): string {
    return '/expired';
  }

  protected get unAuthorizedPagePath(): string {
    return '/403';
  }

  protected get authenticationUrl(): string {
    return apiUrl();
  }
  protected get permissions(): any[] {
    return PERMISSIONS;
  }

  constructor(
    messageService: MessageService,
    eventAggregator: EventAggregator,
    protected translateService: TranslateService,
    localizationService: UMLocalizationService,
    authService: AuthService,
    protected router: Router,
    navigator: Navigator,
    location: Location,
    private browserHistoryAndTitleManager: BrowserHistoryAndTitleManager,
    private breadCrumbService: BreadCrumbService,
    private userService: UserService,
    private route: ActivatedRoute,
    private injector: Injector,
  ) {
    super(
      translateService,
      authService,
      router,
      localizationService as any,
      navigator,
      location,
      messageService,
      eventAggregator,
    );

    this.exportUsers$
      .pipe(
        exhaustMap(() => this.doExportUsers()),
        tap(() => {}),
      )
      .subscribe();
  }
  onBeforeInit(): void {
    // Setup Clarity Icons
    ClarityIcons.add({
      organization: ClrShapeOrganization,
      caret: ClrShapeAngle,
      bars: ClrShapeBars,
      angle: ClrShapeAngle,
      link: ClrShapeLink,
      unlink: ClrShapeUnlink,
      arrow: ClrShapeArrow,
      history: ClrShapeHistory,
      import: ClrShapeImport,
      'error-standard': ClrShapeErrorStandard,
    });

    // Setup Navigator
    this.browserHistoryAndTitleManager.initialize(tr('Usermanagement'));
    EntityResolverService.registerResolvers(entityResolvers);

    registerLocaleData(de);
    registerLocaleData(fr);

    this.switchTenant = this.getQueryParameterByName('switchTenant');

    if (this.switchTenant) {
      this.authService.logout(true);
    }
    this.setAuthenticationConfigTenant();
  }

  // Function to extract query params, cannot use the router yet when the application is still starting up
  getQueryParameterByName(name: any): string | null {
    const url = window.location.href;
    name = name.replace(/[[]]/g, '$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    // return decodeURIComponent(results[2].replace(/+/g, ' '));
    return decodeURIComponent(results[2].replace('/+/g', ' '));
  }

  onInitialized(): Observable<boolean> {
    this.tenantName = this.authService.getTenant().name;

    this.setAuthenticationConfigTenant();

    // console.log(this.authService.getIdentity().upn);
    if (this.authService.getIdentity().upn) {
      this.userService.getById(new Key<string>(this.authService.getIdentity().upn)).subscribe((user) => {
        this.user = user;
      });
    }
    // Use injector to lazy load TenantService, since the app needs to be authenticated before it can be constructed
    const tenantService = this.injector.get(TenantService) as TenantService;
    tenantService.getAllTenantsForCurrentUser().subscribe((result) => {
      this.userHasMoreTenants = result.length > 1;
    });

    // Setup BreadCrumb
    this.crumbs$ = this.breadCrumbService.crumbs$.pipe(
      filter((crumbs: Crumb[]) => {
        return crumbs && crumbs.length > 0;
      }),
    );

    return of(true);
  }

  onNavigateFromCrumb($event: Crumb): void {
    this.navigator.navigateBackInBreadcrumb($event);
  }

  onTenantSelected(tenant: Tenant): void {
    if (tenant.name === this.tenantName) {
      return;
    }
    // add the tenant id to the URL as a queryParam (merging it with potential existing queryParams)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        switchTenant: `${tenant.id}`,
      },
      queryParamsHandling: 'merge',
      // preserve the existing query params in the route
    });
    // Queue a reload event for the entire application (timeout to let the router finish setting the queryParam first)
    setTimeout(() => {
      location.reload();
    }, 0);
  }

  onSetTranslations(): void {
    InputErrorComponent.addErrorMessages({
      email: tr('This field should be a valid email address'),
      upn: tr('This field is not a valid upn. (Must be an email address)'),
      matchPassword: tr('Passwords do not match'),
      userUpnConflict: tr('A user with this UPN already exists'),
      userOtherTenant: tr('A user with this UPN already exists in an other tenant'),
      roleNameConflict: tr('A role with this name already exists'),
      permissionSetNameConflict: tr('A permissionset with this name already exists in this application'),
      permissionSetNameConflictGlobal: tr('A permissionset with this name already exists as a global permissionset'),
      noTemplatePermissionset: tr('This application has no permissions you can import'),
      pattern: tr('The format of this field is invalid.'),
      verificationFailed: tr('Incorrect value'),
    });
  }

  hasAPermission(): boolean {
    if (!this.authService.getIdentity() || !this.menuItems) {
      return true;
    }
    return this.menuItems.some((item) => {
      if (item.permission) {
        return this.authService.getIdentity().hasPermission(item.permission);
      }
      return true;
    });
  }

  hasPermission(permission: string): boolean {
    if (!this.authService.getIdentity()) {
      return false;
    }

    return this.authService.getIdentity().hasPermission(permission);
  }

  onMenuItemClick($event: any): void {
    if ($event.route) {
      this.navigator.navigateRoot([$event.route], { queryParams: { menu: true } });
      return;
    }

    this.navigator.navigateRoot([], { queryParams: { menu: true } });
  }

  onProfileClick(): void {
    this.ddToggle = !this.ddToggle;
  }

  onClickOutsideDropDown(): void {
    this.ddToggle = false;
  }

  get isHeaderVisible(): boolean {
    return this.authService.isAuthenticated();
  }

  get authenticationConfig(): AuthConfig {
    authConfig.issuer = environment.authenticationServer;
    authConfig.scope = environment.scope;

    if (this.switchTenant) {
      if (!authConfig.customQueryParams) {
        authConfig.customQueryParams = {};
      }
      authConfig.customQueryParams[
        'acr_values'
      ] = `${authConfig.customQueryParams['acr_values']} tenant:${this.switchTenant} `;
    }

    log.info('authentication Config', authConfig, environment.authenticationServer);
    return authConfig;
  }

  navigateToBackupRoute(): void {
    this.navigator.navigateRoot([`/me`]);
  }

  protected setAuthenticationConfigTenant(): void {
    let tenantId;
    try {
      tenantId = this.authService.getTenant().key.id;
    } catch {
      return;
    }
    if (!this.authenticationConfig.customQueryParams) {
      this.authenticationConfig.customQueryParams = {};
    }
    let acrValues: string = this.authenticationConfig.customQueryParams['acr_values'];
    if (!!acrValues.includes('tenant')) {
      const guidRegEx = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;
      acrValues = acrValues.replace(guidRegEx, tenantId);
    } else {
      acrValues = `${acrValues} tenant:${tenantId} `;
    }
    this.authenticationConfig.customQueryParams['acr_values'] = acrValues;
  }

  protected onSetMenuItems(): void {
    this.menuItems = [
      {
        route: '/licenses',
        display: tr('Licenses'),
        permission: Permissions.LicenseRead,
      },
      {
        route: '/roles',
        display: tr('Roles'),
        permission: Permissions.RoleRead,
      },
      {
        route: '/users',
        display: tr('Users'),
        permission: Permissions.UserRead,
      },
      {
        route: '/permissionsets',
        display: tr('Permission sets'),
        permission: Permissions.PermissionsetRead,
      },
      {
        route: '/permissions',
        display: tr('Permissions'),
        permission: Permissions.UserManage,
      },
    ];
  }

  protected onInitTranslations(): string {
    UMLocalizationService.addLocales(['fr', 'de']);
    this.translateService.addLangs(['en', 'nl', 'de', 'fr']);
    this.translateService.setDefaultLang('en');
    const browserLang = this.translateService.getBrowserLang();
    return browserLang.match(/en|nl/) ? browserLang : 'en';
  }

  doExportUsers(): Observable<any> {
    this.loading = true;
    return this.userService.exportUsers().pipe(tap(() => (this.loading = false)));
  }
}
